import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { LogIn, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { LogoMark, Wordmark } from "../components/Logo";

export default function Login() {
  const { user, login, loginWithToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [discordUrl, setDiscordUrl] = useState(null);
  const [memberUrl, setMemberUrl] = useState(null);

  useEffect(() => {
    const token = params.get("token");
    const err = params.get("error");
    if (token) {
      loginWithToken(token).then(() => {
        toast.success("Signed in with Discord");
        navigate("/admin", { replace: true });
      }).catch(() => setError("Discord sign-in failed"));
    }
    if (err) {
      setError(err === "not_staff" ? "That Discord account isn't on the GrooveMusic staff team." : "Discord sign-in failed. Try email instead.");
    }
  }, [params, loginWithToken, navigate]);

  useEffect(() => {
    api.get("/auth/discord/url").then((r) => setDiscordUrl(r.data.url)).catch(() => setDiscordUrl(null));
    api.get("/auth/discord/member/url").then((r) => setMemberUrl(r.data.url)).catch(() => setMemberUrl(null));
  }, []);

  useEffect(() => {
    if (user) navigate("/admin", { replace: true });
  }, [user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="min-h-screen flex items-center justify-center hero-glow px-5 py-14" data-testid="login-page">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-3 mb-10" data-testid="login-logo">
          <LogoMark size={36} />
          <Wordmark className="text-xl" />
        </Link>

        {/* Primary: Server Dashboard (server owners & members) */}
        <div className="card-beam rounded-2xl bg-panel border border-blurple/40 p-9 text-center" data-testid="dashboard-login-card">
          <p className="text-xs uppercase tracking-[0.3em] text-volt font-semibold mb-2">Server owners</p>
          <h1 className="font-heading text-2xl font-bold text-white mb-3">Server Dashboard</h1>
          <p className="text-sm text-muted-foreground mb-7 leading-relaxed">
            Manage your servers, prefix, DJ role, volume limits and premium — straight from your Discord account.
          </p>
          {memberUrl ? (
            <a
              href={memberUrl}
              data-testid="dashboard-discord-big"
              className="w-full inline-flex items-center justify-center gap-2.5 bg-blurple hover:bg-[#4752c4] text-white font-heading font-bold py-4 rounded-full text-base transition-colors duration-200 shadow-[0_0_40px_rgba(88,101,242,0.35)]"
            >
              <LogIn size={17} /> Continue with Discord
            </a>
          ) : (
            <p className="text-xs text-white/40 font-mono">Discord sign-in is being configured.</p>
          )}
        </div>

        {/* Secondary: staff admin panel */}
        <div className="rounded-2xl bg-panel/60 border border-white/10 p-7 mt-5" data-testid="login-card">
          <p className="text-xs uppercase tracking-[0.25em] text-white/40 font-semibold mb-1">GrooveMusic staff</p>
          <h2 className="font-heading text-lg font-bold text-white mb-5">Admin Panel Sign In</h2>

          {error && (
            <div className="mb-6 flex items-start gap-3 rounded-lg border border-red-400/30 bg-red-400/10 px-4 py-3 text-sm text-red-300" data-testid="login-error">
              <AlertCircle size={16} className="mt-0.5 shrink-0" /> {error}
            </div>
          )}

          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/50">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                data-testid="login-email-input"
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple"
                placeholder="you@groovemusic.gg"
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.15em] text-white/50">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                autoComplete="current-password"
                data-testid="login-password-input"
                className="mt-1.5 w-full bg-white/5 border border-white/10 rounded-md px-4 py-3 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blurple"
                placeholder="••••••••••"
              />
            </div>
            <button
              type="submit"
              disabled={busy}
              data-testid="login-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 border border-blurple/60 hover:bg-blurple/15 disabled:opacity-50 text-white font-heading font-bold py-3 rounded-full transition-colors duration-200"
            >
              <LogIn size={16} /> {busy ? "Signing in…" : "Staff sign in"}
            </button>
          </form>

          {discordUrl && (
            <>
              <div className="flex items-center gap-4 my-5">
                <span className="flex-1 h-px bg-white/10" />
                <span className="text-xs text-white/30 font-mono">or</span>
                <span className="flex-1 h-px bg-white/10" />
              </div>
              <a
                href={discordUrl}
                data-testid="discord-login-button"
                className="w-full inline-flex items-center justify-center gap-2 border border-white/20 hover:border-white/50 text-white font-heading font-semibold py-3 rounded-full transition-colors duration-200 text-sm"
              >
                Staff — continue with Discord
              </a>
            </>
          )}
          <p className="text-[11px] text-white/35 mt-5 leading-relaxed">
            Being staff in the Discord server does not grant access here — the admin panel is restricted to website staff accounts created by the owner.
          </p>
        </div>

        <p className="text-center text-xs text-white/30 mt-6 font-mono">
          Protected by brute-force lockout · all actions audited
        </p>
      </div>
    </main>
  );
}
